package fr.manyverse.app.presentation.screens

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.slideInHorizontally
import androidx.compose.animation.slideOutHorizontally
import androidx.compose.animation.togetherWith
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Extension
import androidx.compose.material.icons.filled.GraphicEq
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.lifecycle.Lifecycle
import androidx.lifecycle.ViewModelStore
import androidx.lifecycle.ViewModelStoreOwner
import androidx.lifecycle.compose.LifecycleEventEffect
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.lifecycle.viewmodel.compose.LocalViewModelStoreOwner
import fr.manyverse.app.domain.model.ActiveAnnouncement
import fr.manyverse.app.domain.model.ActiveDigicode
import fr.manyverse.app.domain.model.ActiveLockpicking
import fr.manyverse.app.domain.model.ActiveTimer
import fr.manyverse.app.domain.model.ActiveWhiteboard
import fr.manyverse.app.domain.model.Character
import fr.manyverse.app.domain.model.CombatParticipant
import fr.manyverse.app.domain.model.DiceFaceStyle
import fr.manyverse.app.domain.model.DieConfig
import fr.manyverse.app.domain.model.DieType
import fr.manyverse.app.domain.model.DrawPoint
import fr.manyverse.app.domain.model.GameEvent
import fr.manyverse.app.domain.model.GameTable
import fr.manyverse.app.domain.model.GmTimer
import fr.manyverse.app.domain.model.ImageData
import fr.manyverse.app.domain.model.MemberRole
import fr.manyverse.app.domain.model.SoundType
import fr.manyverse.app.domain.model.TableMember
import fr.manyverse.app.domain.model.GameModule
import fr.manyverse.app.infrastructure.gamesession.startGameSessionBackground
import fr.manyverse.app.infrastructure.gamesession.stopGameSessionBackground
import fr.manyverse.app.infrastructure.shake.RememberGuardedShakeDetector
import fr.manyverse.app.presentation.components.DraggableBubble
import fr.manyverse.app.presentation.components.common_tabs.DiceShakeOverlay
import fr.manyverse.app.presentation.components.common_tabs.DiceTab
import fr.manyverse.app.presentation.components.common_tabs.MessagesTab
import fr.manyverse.app.presentation.components.gm_screen.GmPlayerInventorySheet
import fr.manyverse.app.presentation.components.gm_screen.GmPlayerNotesSheet
import fr.manyverse.app.presentation.components.gm_screen.GmPlayerSheetSheet
import fr.manyverse.app.presentation.components.gm_screen.GmSidePanel
import fr.manyverse.app.presentation.components.gm_screen.GmTableTab
import fr.manyverse.app.presentation.components.gm_screen.ModulesTab
import fr.manyverse.app.presentation.components.gm_screen.PlayerAlertGmOverlay
import fr.manyverse.app.presentation.components.gm_screen.SoundsTab
import fr.manyverse.app.presentation.components.modules.combat.CombatBubble
import fr.manyverse.app.presentation.components.modules.combat.CombatGmOverlay
import fr.manyverse.app.presentation.components.modules.combat.CombatPrepareDialog
import fr.manyverse.app.presentation.components.modules.gm_timer.GmTimerBubble
import fr.manyverse.app.presentation.components.modules.whiteboard.WhiteboardBubble
import fr.manyverse.app.presentation.components.modules.whiteboard.WhiteboardGmOverlay
import fr.manyverse.app.presentation.components.notification.NotificationContent
import fr.manyverse.app.presentation.components.notification.NotificationHost
import fr.manyverse.app.presentation.layout.GameScreenScaffold
import fr.manyverse.app.presentation.layout.NavigationItem
import fr.manyverse.app.presentation.state.AsyncUiState
import fr.manyverse.app.presentation.viewmodel.CombatViewModel
import fr.manyverse.app.presentation.viewmodel.DigicodeViewModel
import fr.manyverse.app.presentation.viewmodel.GameMasterViewModel
import fr.manyverse.app.presentation.viewmodel.GmPlayerSheetViewModel
import fr.manyverse.app.presentation.viewmodel.LockpickingViewModel
import fr.manyverse.app.presentation.viewmodel.PlayerNotesViewModel
import fr.manyverse.app.presentation.viewmodel.SoundPlayerViewModel
import fr.manyverse.app.presentation.viewmodel.TimerViewModel
import fr.manyverse.app.presentation.viewmodel.WhiteboardViewModel
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.conversations
import manyverse.shared.generated.resources.dice
import manyverse.shared.generated.resources.icon_dice_1
import manyverse.shared.generated.resources.icon_dice_6
import manyverse.shared.generated.resources.icon_message_in
import manyverse.shared.generated.resources.icon_message_out
import manyverse.shared.generated.resources.icon_open_book
import manyverse.shared.generated.resources.icon_open_page
import manyverse.shared.generated.resources.icon_wave_sound
import manyverse.shared.generated.resources.game_master
import manyverse.shared.generated.resources.modules
import manyverse.shared.generated.resources.sounds
import manyverse.shared.generated.resources.table
import org.jetbrains.compose.resources.stringResource
import org.jetbrains.compose.resources.vectorResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import kotlin.random.Random

private enum class GmTab { TABLE, CONVERSATIONS, SOUNDS, MODULES, DICE }

private data class GmDiceSelection(val dieConfig: DieConfig, val count: Int, val modifier: Int)

private data class GmShakeRoll(
    val dieConfig: DieConfig,
    val count: Int,
    val modifier: Int,
    /** Identifie un lancer unique : force le remontage de l'overlay à chaque secouage. */
    val rollId: Long = Random.nextLong()
)

@Composable
fun GameMasterScreen(
    table: GameTable,
    characters: List<Character>,
    tableMembers: List<TableMember>,
    universeId: String,
    currentUserId: String,
    onNavigateBack: () -> Unit,
    onNavigateToPreMades: (tableId: String) -> Unit,
    onNavigateToGmInventory: (tableId: String) -> Unit
) {
    var selectedTabIndex by remember { mutableIntStateOf(0) }
    var selectedInventoryCharacter by remember { mutableStateOf<Character?>(null) }
    var selectedNotesCharacter by remember { mutableStateOf<Character?>(null) }
    var selectedSheetCharacter by remember { mutableStateOf<Character?>(null) }
    var shakeRollResult by remember { mutableStateOf<GmShakeRoll?>(null) }
    // Les dés de l'overlay roulent encore : un nouveau secouage ne doit pas les couper.
    var shakeRollRolling by remember { mutableStateOf(false) }
    var diceSelection by remember { mutableStateOf<GmDiceSelection?>(null) }
    var isSidePanelOpen by remember { mutableStateOf(false) }
    // Conversation à ouvrir dans l'onglet Conversations (appui sur une notification de message)
    var requestedConversationKey by remember { mutableStateOf<String?>(null) }

    val universeConfig = remember(table.id) { table.resolvedConfig }
    val features = universeConfig.features
    val availableModules = universeConfig.modules
    val availableDice = remember(universeConfig) {
        universeConfig.dice.available.map { it.toDieConfig(universeConfig.dice.highValuesWin) }
    }
    val defaultDie = universeConfig.dice.defaultDie
    val defaultDiceCount = universeConfig.dice.defaultCount
    val multiDiceEnabled = universeConfig.dice.multiEnabled
    val modifiersEnabled = universeConfig.dice.modifiersEnabled
    val diceTotalEnabled = universeConfig.dice.totalEnabled
    val diceFaceStyle = universeConfig.dice.faceStyle

    val viewModel: GameMasterViewModel =
        koinViewModel { parametersOf(table.id, currentUserId, availableDice) }
    val soundPlayerViewModel: SoundPlayerViewModel =
        koinViewModel { parametersOf(universeId, currentUserId) }
    val soundUiState by soundPlayerViewModel.uiState.collectAsStateWithLifecycle()
    val nowPlayingMusic = soundUiState.currentSound?.takeIf { it.type == SoundType.MUSIC }

    val events by viewModel.events.collectAsStateWithLifecycle()
    val eventsState by viewModel.eventsState.collectAsStateWithLifecycle()
    val hasMoreEvents by viewModel.hasMoreEvents.collectAsStateWithLifecycle()
    val loadMoreEventsState by viewModel.loadMoreEventsState.collectAsStateWithLifecycle()
    val announcementState by viewModel.announcementState.collectAsStateWithLifecycle()
    val gmActiveAnnouncements by viewModel.activeAnnouncements.collectAsStateWithLifecycle()
    val kickState by viewModel.kickState.collectAsStateWithLifecycle()
    val deleteTableState by viewModel.deleteTableState.collectAsStateWithLifecycle()
    val renameTableState by viewModel.renameTableState.collectAsStateWithLifecycle()
    val notifications by viewModel.notifications.collectAsStateWithLifecycle()
    val unreadMessageCount by viewModel.unreadMessageCount.collectAsStateWithLifecycle()
    val activePlayerAlert by viewModel.activePlayerAlert.collectAsStateWithLifecycle()
    val messages by viewModel.messages.collectAsStateWithLifecycle()
    val hasMoreOlderMessages by viewModel.hasMoreOlderMessages.collectAsStateWithLifecycle()
    val loadMoreMessagesState by viewModel.loadMoreMessagesState.collectAsStateWithLifecycle()

    val diceConfigState by viewModel.savedDiceConfig.collectAsStateWithLifecycle()
    val storedDice = (diceConfigState as? AsyncUiState.Success)?.data
    val diceConfigLoaded = diceConfigState !is AsyncUiState.Loading
    val effectiveDefaultDie =
        storedDice?.let { s -> availableDice.firstOrNull { it.type.name == s.dieType }?.type } ?: defaultDie
    val effectiveDefaultCount = storedDice?.count?.takeIf { multiDiceEnabled } ?: defaultDiceCount
    val effectiveDefaultModifier = storedDice?.modifier?.takeIf { modifiersEnabled } ?: 0

    LaunchedEffect(diceConfigState) {
        val s = storedDice ?: return@LaunchedEffect
        val cfg = availableDice.firstOrNull { it.type.name == s.dieType } ?: return@LaunchedEffect
        diceSelection = GmDiceSelection(cfg, effectiveDefaultCount, effectiveDefaultModifier)
    }

    LaunchedEffect(deleteTableState) {
        if (deleteTableState is AsyncUiState.Success) {
            onNavigateBack()
        }
    }

    DisposableEffect(Unit) {
        startGameSessionBackground()
        onDispose { stopGameSessionBackground() }
    }

    // Whiteboard ViewModel
    val whiteboardViewModel: WhiteboardViewModel = koinViewModel()
    val whiteboardLaunchState by whiteboardViewModel.launchState.collectAsStateWithLifecycle()
    val whiteboardState by whiteboardViewModel.whiteboardState.collectAsStateWithLifecycle()
    val selectedColor by whiteboardViewModel.selectedColor.collectAsStateWithLifecycle()
    val strokeWidth by whiteboardViewModel.strokeWidth.collectAsStateWithLifecycle()
    val selectedTool by whiteboardViewModel.selectedTool.collectAsStateWithLifecycle()

    // Combat ViewModel
    val combatViewModel: CombatViewModel = koinViewModel()
    val combatState by combatViewModel.state.collectAsStateWithLifecycle()

    // Timer ViewModel
    val timerViewModel: TimerViewModel = koinViewModel()
    val timerLaunchState by timerViewModel.launchState.collectAsStateWithLifecycle()
    val gmActiveTimers by timerViewModel.activeTimers.collectAsStateWithLifecycle()
    val gmTimers by timerViewModel.gmTimers.collectAsStateWithLifecycle()

    // Digicode ViewModel
    val digicodeViewModel: DigicodeViewModel = koinViewModel()
    val digicodeState by digicodeViewModel.launchState.collectAsStateWithLifecycle()
    val gmActiveDigicodes by digicodeViewModel.gmActiveDigicodes.collectAsStateWithLifecycle()

    // Lockpicking ViewModel
    val lockpickingViewModel: LockpickingViewModel = koinViewModel()
    val lockpickingState by lockpickingViewModel.launchState.collectAsStateWithLifecycle()
    val gmActiveLockpicking by lockpickingViewModel.activeLockpicking.collectAsStateWithLifecycle()

    val playersCount = remember(tableMembers) {
        tableMembers.count { it.role == MemberRole.PLAYER }
    }

    val gmLabel = stringResource(Res.string.game_master)

    // Rattrape ce que le temps réel n'a pas transmis : appli en arrière-plan ou coupure réseau
    fun resync(afterReconnection: Boolean) {
        viewModel.refreshOnForeground(afterReconnection)
        whiteboardViewModel.refreshOnForeground(table.id, currentUserId)
        combatViewModel.startObservingActiveCombat(table.id, currentUserId)
        combatViewModel.refreshOnForeground(table.id, currentUserId)
        timerViewModel.startObservingForGm(table.id)
        digicodeViewModel.startObservingForGm(table.id)
        lockpickingViewModel.startObservingForGm(table.id)
    }

    LifecycleEventEffect(Lifecycle.Event.ON_RESUME) { resync(afterReconnection = false) }

    LaunchedEffect(viewModel) {
        viewModel.reconnections.collect { resync(afterReconnection = true) }
    }

    LifecycleEventEffect(Lifecycle.Event.ON_PAUSE) {
        viewModel.markAllNotificationsSeen()
    }

    // Shake-to-roll
    // Le secouage ne fait qu'ouvrir l'overlay : le résultat vient des dés, qui le publieront
    // eux-mêmes en se couchant.
    RememberGuardedShakeDetector(
        // Univers sans dés : pas d'onglet Dés, donc pas de lancer au secouage non plus.
        enabled = features.dice,
        onShake = {
            // Tant que les dés n'ont pas donné leur résultat, secouer ne relance rien.
            if (shakeRollRolling) return@RememberGuardedShakeDetector
            val selection = diceSelection
            val dieConfig = selection?.dieConfig ?: availableDice.firstOrNull { it.type == defaultDie }
            val count = selection?.count ?: defaultDiceCount
            if (dieConfig != null) {
                shakeRollResult = GmShakeRoll(dieConfig, count, selection?.modifier ?: 0)
                shakeRollRolling = true
            }
        }
    )

    val visibleTabs = buildList {
        add(GmTab.TABLE)
        if (features.messages) add(GmTab.CONVERSATIONS)
        if (features.soundboard) add(GmTab.SOUNDS)
        if (availableModules.isNotEmpty()) add(GmTab.MODULES)
        if (features.dice) add(GmTab.DICE)
    }
    val navigationItems = visibleTabs.map { tab ->
        when (tab) {
            GmTab.TABLE -> NavigationItem(
                label = stringResource(Res.string.table),
                icon = vectorResource(Res.drawable.icon_open_book),
                selectedIcon = vectorResource(Res.drawable.icon_open_page)
            )
            GmTab.CONVERSATIONS -> NavigationItem(
                label = stringResource(Res.string.conversations),
                icon = vectorResource(Res.drawable.icon_message_out),
                selectedIcon = vectorResource(Res.drawable.icon_message_in),
                badgeCount = unreadMessageCount
            )
            GmTab.SOUNDS -> NavigationItem(
                label = stringResource(Res.string.sounds),
                icon = Icons.Default.GraphicEq,
                selectedIcon = vectorResource(Res.drawable.icon_wave_sound)
            )
            GmTab.MODULES -> NavigationItem(
                label = stringResource(Res.string.modules),
                icon = Icons.Default.Extension,
                selectedIconRotationDegrees = 90f
            )
            GmTab.DICE -> NavigationItem(
                label = stringResource(Res.string.dice),
                icon = vectorResource(Res.drawable.icon_dice_1),
                selectedIcon = vectorResource(Res.drawable.icon_dice_6)
            )
        }
    }
    val messagesTabIndex = visibleTabs.indexOf(GmTab.CONVERSATIONS)

    LaunchedEffect(visibleTabs.size) {
        if (selectedTabIndex >= visibleTabs.size) selectedTabIndex = 0
    }

    // Lambdas stabilisées via remember : sans cela, chaque émission de StateFlow
    // recompose le screen entier et recrée les lambdas, ce qui invalide les
    // composables enfants même quand leurs props effectives n'ont pas changé.
    val onInventoryClick = remember { { c: Character -> selectedInventoryCharacter = c } }
    val onNotesClick = remember { { c: Character -> selectedNotesCharacter = c } }
    val onSheetClick = remember { { c: Character -> selectedSheetCharacter = c } }
    val onDeleteTable = remember(viewModel, tableMembers) {
        {
            viewModel.deleteTable(
                tableMembers.filter { it.role == MemberRole.PLAYER }.map { it.userId }
            )
        }
    }
    val onManagePreMades = remember(onNavigateToPreMades, table.id) {
        { onNavigateToPreMades(table.id) }
    }
    val onGmInventoryClick = remember(onNavigateToGmInventory, table.id) {
        { onNavigateToGmInventory(table.id) }
    }
    val onSendMessage = remember(viewModel, gmLabel) {
        { text: String, ids: List<String> -> viewModel.sendMessage(text, ids, gmLabel) }
    }
    val onCreateAnnouncement = remember(viewModel) {
        { text: String?, image: ImageData?, targets: List<String> ->
            viewModel.createAnnouncement(text, image, targets)
        }
    }
    val onOpenWhiteboard = remember(whiteboardViewModel, table.id, currentUserId) {
        { participants: List<String>, showGrid: Boolean ->
            whiteboardViewModel.openWhiteboard(
                tableId = table.id,
                createdBy = currentUserId,
                targetParticipants = participants,
                showGrid = showGrid
            )
        }
    }
    val onCreateTimer = remember(timerViewModel, table.id, currentUserId) {
        { durationSeconds: Int, targets: List<String> ->
            timerViewModel.createTimer(
                tableId = table.id,
                durationSeconds = durationSeconds,
                targetParticipants = targets,
                createdBy = currentUserId
            )
        }
    }
    val onCreateDigicode = remember(digicodeViewModel, table.id, currentUserId) {
        { targetId: String, digitCount: Int, maxAttempts: Int, timeLimitSeconds: Int?, showAttempts: Boolean, soundEnabled: Boolean, solution: String ->
            digicodeViewModel.create(
                tableId = table.id,
                targetParticipantId = targetId,
                digitCount = digitCount,
                maxAttempts = maxAttempts,
                timeLimitSeconds = timeLimitSeconds,
                showAttemptsToPlayer = showAttempts,
                soundEnabled = soundEnabled,
                solution = solution,
                createdBy = currentUserId
            )
        }
    }
    val onCreateLockpicking = remember(lockpickingViewModel, table.id, currentUserId) {
        { targetId: String, lockCount: Int, timeLimitSeconds: Int?, showLockCount: Boolean, soundEnabled: Boolean ->
            lockpickingViewModel.create(
                tableId = table.id,
                targetParticipantId = targetId,
                lockCount = lockCount,
                timeLimitSeconds = timeLimitSeconds,
                showLockCount = showLockCount,
                soundEnabled = soundEnabled,
                createdBy = currentUserId
            )
        }
    }
    val onCancelLockpicking = remember(lockpickingViewModel) {
        {
            val lockpicking = gmActiveLockpicking
            if (lockpicking != null) {
                lockpickingViewModel.cancel(lockpickingId = lockpicking.id)
            }
            Unit
        }
    }
    val onRollCompleted = remember(viewModel, gmLabel) {
        { dieConfig: DieConfig, count: Int, results: List<Int>, modifier: Int ->
            viewModel.createDiceRollEvent(
                characterName = gmLabel,
                dieType = dieConfig.type,
                count = count,
                results = results,
                isGmRoll = true,
                modifier = modifier
            )
        }
    }

    // Overlay & bottom-sheet lambdas stabilisées
    val onTabSelected = remember(viewModel) {
        { index: Int ->
            selectedTabIndex = index
            if (index == messagesTabIndex) {
                viewModel.clearUnreadMessages()
            }
        }
    }
    val onDismissNotification = remember(viewModel) {
        { notif: NotificationContent -> viewModel.dismissNotification(notif.id) }
    }
    // Appui sur une notification de message : réduit ce qui masque l'onglet Conversations
    // (tableau blanc, combat, panneau MJ) puis ouvre la conversation concernée.
    val onNotificationClick = remember(viewModel, whiteboardViewModel, combatViewModel, messagesTabIndex) {
        { notif: NotificationContent ->
            if (notif is NotificationContent.NewMessage && messagesTabIndex >= 0) {
                viewModel.dismissNotification(notif.id)
                if (whiteboardState.isOpen) whiteboardViewModel.minimize()
                if (combatState.isExpanded) combatViewModel.minimize()
                isSidePanelOpen = false
                requestedConversationKey = notif.conversationKey
                selectedTabIndex = messagesTabIndex
                viewModel.clearUnreadMessages()
            }
        }
    }
    val onConversationRequestHandled = remember { { requestedConversationKey = null } }
    val onDismissInventorySheet = remember { { selectedInventoryCharacter = null } }
    val onDismissNotesSheet = remember { { selectedNotesCharacter = null } }
    val onDismissSheetSheet = remember { { selectedSheetCharacter = null } }
    val onDismissShakeOverlay = remember {
        {
            shakeRollResult = null
            shakeRollRolling = false
        }
    }
    val onDismissPlayerAlert = remember(viewModel) { { viewModel.dismissPlayerAlert() } }
    val onStrokeComplete = remember(whiteboardViewModel, table.id) {
        { points: List<DrawPoint> -> whiteboardViewModel.drawStroke(table.id, points) }
    }
    val onToggleGrid = remember(whiteboardViewModel, table.id) {
        { whiteboardViewModel.toggleGrid(table.id) }
    }
    val onClearAll = remember(whiteboardViewModel, table.id) {
        { whiteboardViewModel.clearAll(table.id) }
    }
    val onCloseForAll = remember(whiteboardViewModel, table.id) {
        { whiteboardViewModel.closeForAll(table.id) }
    }

    Box(modifier = Modifier.fillMaxSize()) {
        GameScreenScaffold(
            navigationItems = navigationItems,
            selectedTabIndex = selectedTabIndex,
            onTabSelected = onTabSelected,
            overlayContent = {
                GmBubbleOverlays(
                    gmTimers = gmTimers,
                    whiteboardIsMinimized = whiteboardState.isMinimized,
                    timerViewModel = timerViewModel,
                    onRestoreWhiteboard = whiteboardViewModel::restore,
                    combatIsMinimized = combatState.isMinimized,
                    combatActiveParticipant = combatState.combat?.activeParticipant,
                    combatCharacters = characters,
                    onRestoreCombat = combatViewModel::restore,
                    onNextCombatTurn = combatViewModel::nextTurn
                )
            }
        ) {
            GameMasterTabs(
                selectedTab = visibleTabs.getOrElse(selectedTabIndex) { GmTab.TABLE },
                availableModules = availableModules,
                table = table,
                characters = characters,
                tableMembers = tableMembers,
                currentUserId = currentUserId,
                universeId = universeId,
                isInventoryAvailable = features.inventory,
                isCharacterSheetAvailable = features.characterSheet,
                playersCount = playersCount,
                events = events,
                eventsState = eventsState,
                hasMoreEvents = hasMoreEvents,
                loadMoreEventsState = loadMoreEventsState,
                kickState = kickState,
                deleteTableState = deleteTableState,
                renameTableState = renameTableState,
                messages = messages,
                hasMoreOlderMessages = hasMoreOlderMessages,
                isLoadingOlderMessages = loadMoreMessagesState is AsyncUiState.Loading,
                onLoadOlderMessages = viewModel::loadOlderMessages,
                requestedConversationKey = requestedConversationKey,
                onConversationRequestHandled = onConversationRequestHandled,
                announcementState = announcementState,
                gmActiveAnnouncements = gmActiveAnnouncements,
                whiteboardLaunchState = whiteboardLaunchState,
                whiteboardIsMinimized = whiteboardState.isMinimized,
                timerLaunchState = timerLaunchState,
                gmActiveTimers = gmActiveTimers,
                digicodeState = digicodeState,
                gmActiveDigicodes = gmActiveDigicodes,
                lockpickingState = lockpickingState,
                gmActiveLockpicking = gmActiveLockpicking,
                availableDice = availableDice,
                defaultDie = effectiveDefaultDie,
                multiDiceEnabled = multiDiceEnabled,
                modifiersEnabled = modifiersEnabled,
                totalEnabled = diceTotalEnabled,
                faceStyle = diceFaceStyle,
                defaultDiceCount = effectiveDefaultCount,
                defaultModifier = effectiveDefaultModifier,
                diceConfigLoaded = diceConfigLoaded,
                shakeRollActive = shakeRollResult != null,
                viewModel = viewModel,
                timerViewModel = timerViewModel,
                digicodeViewModel = digicodeViewModel,
                lockpickingViewModel = lockpickingViewModel,
                whiteboardViewModel = whiteboardViewModel,
                soundPlayerViewModel = soundPlayerViewModel,
                onInventoryClick = onInventoryClick,
                onNotesClick = onNotesClick,
                onSheetClick = onSheetClick,
                onDeleteTable = onDeleteTable,
                onNavigateBack = onNavigateBack,
                onManagePreMades = onManagePreMades,
                onGmInventoryClick = onGmInventoryClick,
                onSendMessage = onSendMessage,
                onCreateAnnouncement = onCreateAnnouncement,
                onOpenWhiteboard = onOpenWhiteboard,
                onCreateTimer = onCreateTimer,
                onCreateDigicode = onCreateDigicode,
                onCreateLockpicking = onCreateLockpicking,
                onCancelLockpicking = onCancelLockpicking,
                combatIsActive = combatState.combat != null,
                onOpenCombatPrepare = combatViewModel::openPrepare,
                onRestoreCombat = combatViewModel::restore,
                onRollCompleted = onRollCompleted,
                onDiceConfigChanged = { dieConfig, count, mod ->
                    diceSelection = GmDiceSelection(dieConfig, count, mod)
                    viewModel.saveDiceConfig(dieConfig.type, count, mod)
                }
            )
        }

        // GM inventory sheet
        selectedInventoryCharacter?.let { character ->
            GmPlayerInventorySheet(
                character = character,
                tableId = table.id,
                currentUserId = currentUserId,
                tableMembers = tableMembers,
                characters = characters,
                onDismiss = onDismissInventorySheet
            )
        }

        // GM player notes sheet
        selectedNotesCharacter?.let { character ->
            tableMembers.find { it.characterId == character.id }?.let { player ->
                val notesViewModel: PlayerNotesViewModel = koinViewModel(
                    key = "notes_${table.id}_${player.userId}"
                ) { parametersOf(table.id, player.userId) }
                GmPlayerNotesSheet(
                    characterName = character.name,
                    viewModel = notesViewModel,
                    onDismiss = onDismissNotesSheet
                )
            }
        }

        // GM player character sheet (read-only) — re-fetch frais en base, le sheet n'est pas
        // synchronisé par Realtime (cf. GmPlayerSheetHost).
        selectedSheetCharacter?.let { character ->
            GmPlayerSheetHost(
                character = character,
                tableId = table.id,
                gmUserId = currentUserId,
                onDismiss = onDismissSheetSheet
            )
        }

        // Whiteboard overlay
        WhiteboardGmOverlay(
            isVisible = whiteboardState.isOpen,
            isGM = true,
            strokes = whiteboardState.strokes,
            showGrid = whiteboardState.showGrid,
            selectedColor = selectedColor,
            strokeWidth = strokeWidth,
            selectedTool = selectedTool,
            onStrokeComplete = onStrokeComplete,
            onColorSelect = whiteboardViewModel::setColor,
            onToolSelect = whiteboardViewModel::setTool,
            onToggleGrid = onToggleGrid,
            onClearAll = onClearAll,
            onMinimize = whiteboardViewModel::minimize,
            onCloseForAll = onCloseForAll
        )

        // Combat prepare overlay (GM)
        CombatPrepareDialog(
            isVisible = combatState.isPrepareOpen,
            tableMembers = tableMembers,
            characters = characters,
            onDismiss = combatViewModel::closePrepare,
            onLaunch = { participants ->
                combatViewModel.startCombat(table.id, currentUserId, participants)
            }
        )

        // Combat overlay (GM "it's their turn")
        combatState.combat?.let { combat ->
            CombatGmOverlay(
                isVisible = combatState.isExpanded,
                combat = combat,
                characters = characters,
                onNextTurn = combatViewModel::nextTurn,
                onMoveUp = { index -> combatViewModel.moveParticipant(index, index - 1) },
                onMoveDown = { index -> combatViewModel.moveParticipant(index, index + 1) },
                onRemove = combatViewModel::removeParticipant,
                onMinimize = combatViewModel::minimize,
                onStop = combatViewModel::stopCombat
            )
        }

        // GM side panel (tools + running GM timers) — openable from any tab and
        // déclaré après le tableau blanc et le combat pour rester accessible par-dessus.
        GmSidePanel(
            isOpen = isSidePanelOpen,
            onOpenChange = { isSidePanelOpen = it },
            gmTimers = gmTimers,
            onGmInventoryClick = onGmInventoryClick,
            onTimerPause = timerViewModel::pauseGmTimer,
            onTimerStop = timerViewModel::stopGmTimer,
            nowPlayingMusic = nowPlayingMusic,
            isMusicPlaying = soundUiState.playbackState.isPlaying,
            positionMs = soundUiState.playbackState.currentPositionMs,
            durationMs = soundUiState.playbackState.durationMs,
            showNextButton = soundUiState.autoPlayTag != null,
            onMusicPlayPause = soundPlayerViewModel::togglePlayPause,
            onMusicNext = soundPlayerViewModel::playNextTrack
        )

        // Ordre des overlays (le dernier déclaré passe devant) : tableau blanc < combat < panneau MJ
        // < retours brefs (notifications, dés) < alerte de sécurité. Les bottom sheets et la
        // préparation du combat sont des Dialog : toujours au-dessus.

        // Dice roll notifications (full-width, top)
        NotificationHost(
            notifications = notifications,
            onDismiss = onDismissNotification,
            onClick = onNotificationClick,
            modifier = Modifier
                .align(Alignment.TopCenter)
                .statusBarsPadding()
                .fillMaxWidth()
                .padding(start = 12.dp, top = 8.dp, end = 12.dp),
            diceTotalEnabled = diceTotalEnabled
        )

        // Shake-to-roll overlay : rien à afficher si l'univers n'a pas les dés.
        shakeRollResult?.takeIf { features.dice }?.let { roll ->
            // Remonte l'overlay à chaque nouveau secouage : sans cette clé, Compose recompose
            // l'instance existante et les dés 3D restent figés sur le lancer précédent.
            key(roll.rollId) {
                DiceShakeOverlay(
                    dieConfig = roll.dieConfig,
                    count = roll.count,
                    onRolled = { results ->
                        shakeRollRolling = false
                        viewModel.createDiceRollEvent(
                            characterName = gmLabel,
                            dieType = roll.dieConfig.type,
                            count = roll.count,
                            results = results,
                            isGmRoll = true,
                            modifier = roll.modifier
                        )
                    },
                    rollModifier = roll.modifier,
                    totalEnabled = diceTotalEnabled,
                    faceStyle = diceFaceStyle,
                    onDismiss = onDismissShakeOverlay,
                    modifier = Modifier.fillMaxSize()
                )
            }
        }

        // Player safety alert overlay
        activePlayerAlert?.let { alert ->
            PlayerAlertGmOverlay(
                alert = alert,
                onDismiss = onDismissPlayerAlert
            )
        }
    }
}

// La fiche personnage (compétences / caractéristiques / jauges) n'est pas synchronisée par
// Realtime : on re-fetche les données à jour en base à chaque ouverture via un ViewModel dédié,
// isolé dans son propre ViewModelStoreOwner pour être détruit à la fermeture de la sheet.
// Le sheet en cache sert de placeholder instantané, remplacé dès que les données fraîches arrivent.
@Composable
private fun GmPlayerSheetHost(
    character: Character,
    tableId: String,
    gmUserId: String,
    onDismiss: () -> Unit
) {
    val storeOwner = remember(character.id) {
        object : ViewModelStoreOwner {
            override val viewModelStore = ViewModelStore()
        }
    }
    DisposableEffect(storeOwner) {
        onDispose { storeOwner.viewModelStore.clear() }
    }
    CompositionLocalProvider(LocalViewModelStoreOwner provides storeOwner) {
        val sheetViewModel: GmPlayerSheetViewModel =
            koinViewModel(key = character.id) { parametersOf(character.id, tableId, gmUserId) }
        val sheetState by sheetViewModel.sheetState.collectAsStateWithLifecycle()
        val displaySheet = (sheetState as? AsyncUiState.Success)?.data ?: character.sheet
        GmPlayerSheetSheet(
            characterName = character.name,
            sheet = displaySheet,
            onIncrement = sheetViewModel::incrementGauge,
            onDecrement = sheetViewModel::decrementGauge,
            onDismiss = onDismiss
        )
    }
}

// Bulles draggables extraites pour isoler leur état local (timerOffset / whiteboardOffset)
// et leurs lambdas : sans cela, chaque émission d'une StateFlow du parent invalidait
// l'overlay entier et recréait les lambdas inline.
@Composable
private fun BoxScope.GmBubbleOverlays(
    gmTimers: List<GmTimer>,
    whiteboardIsMinimized: Boolean,
    timerViewModel: TimerViewModel,
    onRestoreWhiteboard: () -> Unit,
    combatIsMinimized: Boolean,
    combatActiveParticipant: CombatParticipant?,
    combatCharacters: List<Character>,
    onRestoreCombat: () -> Unit,
    onNextCombatTurn: () -> Unit
) {
    BoxWithConstraints(Modifier.fillMaxSize()) {
        val cW = constraints.maxWidth
        val cH = constraints.maxHeight
        gmTimers.forEachIndexed { index, timer ->
            key(timer.id) {
                DraggableBubble(
                    alignment = Alignment.BottomEnd,
                    bottomPadding = (100 + index * 60).dp,
                    containerWidth = cW,
                    containerHeight = cH,
                    dragKey = timer.id
                ) {
                    GmTimerBubble(
                        timer = timer,
                        onTick = timerViewModel::tickGmTimer,
                        onPause = remember(timerViewModel, timer.id) {
                            { timerViewModel.pauseGmTimer(timer.id) }
                        },
                        onStop = remember(timerViewModel, timer.id) {
                            { timerViewModel.stopGmTimer(timer.id) }
                        }
                    )
                }
            }
        }
        if (whiteboardIsMinimized) {
            DraggableBubble(
                alignment = Alignment.BottomStart,
                bottomPadding = 32.dp,
                containerWidth = cW,
                containerHeight = cH,
                dragKey = Unit
            ) {
                WhiteboardBubble(onClick = onRestoreWhiteboard)
            }
        }
        if (combatIsMinimized) {
            DraggableBubble(
                alignment = Alignment.BottomStart,
                bottomPadding = 100.dp,
                containerWidth = cW,
                containerHeight = cH,
                dragKey = "combat"
            ) {
                CombatBubble(
                    activeParticipant = combatActiveParticipant,
                    characters = combatCharacters,
                    onClick = onRestoreCombat,
                    onDoubleClick = onNextCombatTurn
                )
            }
        }
    }
}

// Tabs extraites en composable top-level : la closure de capture est désormais
// explicite via la signature. Sans cela, chaque émission de StateFlow du parent
// recréait la fonction interne TabContent et invalidait le bloc AnimatedContent
// même quand le tab visible n'était pas concerné par le state qui a changé.
@Composable
private fun GameMasterTabs(
    selectedTab: GmTab,
    availableModules: Set<GameModule>,
    table: GameTable,
    characters: List<Character>,
    tableMembers: List<TableMember>,
    currentUserId: String,
    universeId: String,
    isInventoryAvailable: Boolean,
    isCharacterSheetAvailable: Boolean,
    playersCount: Int,
    events: List<GameEvent>,
    eventsState: AsyncUiState<List<GameEvent>>,
    hasMoreEvents: Boolean,
    loadMoreEventsState: AsyncUiState<Unit>,
    kickState: AsyncUiState<Unit>,
    deleteTableState: AsyncUiState<Unit>,
    renameTableState: AsyncUiState<Unit>,
    messages: List<GameEvent>,
    hasMoreOlderMessages: Boolean,
    isLoadingOlderMessages: Boolean,
    onLoadOlderMessages: () -> Unit,
    requestedConversationKey: String?,
    onConversationRequestHandled: () -> Unit,
    announcementState: AsyncUiState<GameEvent>,
    gmActiveAnnouncements: List<ActiveAnnouncement>,
    whiteboardLaunchState: AsyncUiState<ActiveWhiteboard>,
    whiteboardIsMinimized: Boolean,
    timerLaunchState: AsyncUiState<ActiveTimer>,
    gmActiveTimers: List<ActiveTimer>,
    digicodeState: AsyncUiState<ActiveDigicode>,
    gmActiveDigicodes: List<ActiveDigicode>,
    lockpickingState: AsyncUiState<ActiveLockpicking>,
    gmActiveLockpicking: ActiveLockpicking?,
    availableDice: List<DieConfig>,
    defaultDie: DieType,
    multiDiceEnabled: Boolean,
    modifiersEnabled: Boolean,
    totalEnabled: Boolean,
    faceStyle: DiceFaceStyle,
    defaultDiceCount: Int,
    defaultModifier: Int,
    diceConfigLoaded: Boolean,
    shakeRollActive: Boolean,
    viewModel: GameMasterViewModel,
    timerViewModel: TimerViewModel,
    digicodeViewModel: DigicodeViewModel,
    lockpickingViewModel: LockpickingViewModel,
    whiteboardViewModel: WhiteboardViewModel,
    soundPlayerViewModel: SoundPlayerViewModel,
    onInventoryClick: (Character) -> Unit,
    onNotesClick: (Character) -> Unit,
    onSheetClick: (Character) -> Unit,
    onDeleteTable: () -> Unit,
    onNavigateBack: () -> Unit,
    onManagePreMades: () -> Unit,
    onGmInventoryClick: () -> Unit,
    onSendMessage: (String, List<String>) -> Unit,
    onCreateAnnouncement: (String?, ImageData?, List<String>) -> Unit,
    onOpenWhiteboard: (List<String>, Boolean) -> Unit,
    onCreateTimer: (Int, List<String>) -> Unit,
    onCreateDigicode: (String, Int, Int, Int?, Boolean, Boolean, String) -> Unit,
    onCreateLockpicking: (String, Int, Int?, Boolean, Boolean) -> Unit,
    onCancelLockpicking: () -> Unit,
    combatIsActive: Boolean,
    onOpenCombatPrepare: () -> Unit,
    onRestoreCombat: () -> Unit,
    onRollCompleted: (DieConfig, Int, List<Int>, Int) -> Unit,
    onDiceConfigChanged: (DieConfig, Int, Int) -> Unit,
    modifier: Modifier = Modifier
) {
    AnimatedContent(
        targetState = selectedTab,
        transitionSpec = {
            val direction = if (targetState.ordinal > initialState.ordinal) 1 else -1
            (slideInHorizontally(tween(200)) { it * direction } + fadeIn(tween(200))) togetherWith
                    (slideOutHorizontally(tween(200)) { it * -direction } + fadeOut(tween(200)))
        },
        label = "TabTransition"
    ) { tab ->
        when (tab) {
            GmTab.TABLE -> GmTableTab(
                table = table,
                characters = characters,
                onInventoryClick = onInventoryClick,
                showInventoryButton = isInventoryAvailable,
                showSheetButton = isCharacterSheetAvailable,
                onNotesClick = onNotesClick,
                onSheetClick = onSheetClick,
                events = events,
                eventsState = eventsState,
                hasMoreEvents = hasMoreEvents,
                loadMoreEventsState = loadMoreEventsState,
                totalPlayersCount = playersCount,
                tableMembers = tableMembers,
                onLoadEvents = viewModel::loadEvents,
                onLoadMoreEvents = viewModel::loadMoreEvents,
                kickState = kickState,
                onKickPlayer = viewModel::kickPlayer,
                onResetKickState = viewModel::resetKickState,
                deleteTableState = deleteTableState,
                onDeleteTable = onDeleteTable,
                onResetDeleteTableState = viewModel::resetDeleteTableState,
                renameTableState = renameTableState,
                onRenameTable = viewModel::renameTable,
                onResetRenameTableState = viewModel::resetRenameTableState,
                onNavigateBack = onNavigateBack,
                onManagePreMades = onManagePreMades,
                onGmInventoryClick = onGmInventoryClick,
                diceTotalEnabled = totalEnabled
            )

            GmTab.CONVERSATIONS -> MessagesTab(
                messages = messages,
                tableMembers = tableMembers,
                characters = characters,
                currentUserId = currentUserId,
                isGm = true,
                onSendMessage = onSendMessage,
                onSendAlert = { _, message -> viewModel.sendGmAlert(message) },
                hasMoreOlderMessages = hasMoreOlderMessages,
                isLoadingOlderMessages = isLoadingOlderMessages,
                onLoadOlderMessages = onLoadOlderMessages,
                requestedConversationKey = requestedConversationKey,
                onConversationRequestHandled = onConversationRequestHandled,
                modifier = modifier
            )

            GmTab.SOUNDS -> SoundsTab(viewModel = soundPlayerViewModel)

            GmTab.MODULES -> ModulesTab(
                availableModules = availableModules,
                tableMembers = tableMembers,
                characters = characters,
                onCreateAnnouncement = onCreateAnnouncement,
                announcementState = announcementState,
                activeAnnouncements = gmActiveAnnouncements,
                onCancelAnnouncement = viewModel::cancelAnnouncement,
                whiteboardLaunchState = whiteboardLaunchState,
                whiteboardIsMinimized = whiteboardIsMinimized,
                onOpenWhiteboard = onOpenWhiteboard,
                onRestoreWhiteboard = whiteboardViewModel::restore,
                onResetWhiteboardLaunchState = whiteboardViewModel::resetLaunchState,
                timerLaunchState = timerLaunchState,
                activeTimers = gmActiveTimers,
                onCreateTimer = onCreateTimer,
                onStopTimer = timerViewModel::stopTimer,
                onTogglePauseTimer = timerViewModel::togglePauseTimer,
                onTimerExpired = timerViewModel::removeExpiredTimer,
                onResetTimerLaunchState = timerViewModel::resetLaunchState,
                digicodeState = digicodeState,
                activeDigicodes = gmActiveDigicodes,
                onCreateDigicode = onCreateDigicode,
                onCancelDigicode = digicodeViewModel::cancel,
                onCloseDigicode = digicodeViewModel::closeGm,
                onResetDigicodeLaunchState = digicodeViewModel::resetLaunchState,
                lockpickingState = lockpickingState,
                activeLockpicking = gmActiveLockpicking,
                onCreateLockpicking = onCreateLockpicking,
                onCreateGmTimer = timerViewModel::createGmTimer,
                onCancelLockpicking = onCancelLockpicking,
                onCloseLockpicking = lockpickingViewModel::close,
                onResetLockpickingLaunchState = lockpickingViewModel::resetLaunchState,
                combatIsActive = combatIsActive,
                onOpenCombatPrepare = onOpenCombatPrepare,
                onRestoreCombat = onRestoreCombat
            )

            GmTab.DICE -> {
                if (diceConfigLoaded) {
                    DiceTab(
                        availableDice = availableDice,
                        defaultDie = defaultDie,
                        multiDiceEnabled = multiDiceEnabled,
                        modifiersEnabled = modifiersEnabled,
                        totalEnabled = totalEnabled,
                        faceStyle = faceStyle,
                        defaultDiceCount = defaultDiceCount,
                        defaultModifier = defaultModifier,
                        shakeRollActive = shakeRollActive,
                        onRollCompleted = onRollCompleted,
                        onConfigChanged = onDiceConfigChanged,
                        modifier = modifier
                    )
                } else {
                    Box(modifier = modifier.fillMaxSize())
                }
            }
        }
    }
}

